import type { CaseForPharmacy } from "@omony/domain";
import { Badge, Banner, Button, KeyValueList, Select, Spinner } from "@omony/ui";
import { useMutation } from "@tanstack/react-query";
import { ClipboardList, Play, RotateCcw, Stethoscope, XCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { ErrorBanner } from "../../shared/auth/screens.js";
import { api } from "../../shared/api.js";
import { EMPTY } from "../../shared/format.js";
import { queryClient } from "../../shared/query.js";
import { applyCaseResponse, useFeatures, useSmed } from "../data.js";
import { useIdleHold } from "../hooks.js";
import { Spacer, StepFrame, useT } from "./common.js";
import styles from "./flow.module.css";

const OPEN = new Set(["erstellt", "laufend"]);

/**
 * SmED-Ersteinschätzung (A15): kein neuer Tab. Iframe-Variante mit Rückmeldung per postMessage,
 * Redirect-Variante im selben Fenster mit Rücksprung über `/?resume=<vorgang>`.
 */
export function SmedPanel({ c, onFallback }: { c: CaseForPharmacy; onFallback: () => void }) {
  const t = useT();
  const features = useFeatures();
  const smed = useSmed(c.id, !!c.smedSession);
  const [sex, setSex] = useState<"male" | "female" | null>(null);
  const [frameUrl, setFrameUrl] = useState<string | null>(null);
  const session = smed.data ?? null;
  useIdleHold(!!frameUrl);

  // Rückmeldung der Ergebnisseite im Iframe (nur eigene Origin)
  useEffect(() => {
    const on = (e: MessageEvent) => {
      if (e.origin !== location.origin) return;
      const d = e.data as { type?: string } | null;
      if (d?.type !== "omony-smed") return;
      setFrameUrl(null);
      void queryClient.invalidateQueries({ queryKey: ["smed", c.id] });
      void queryClient.invalidateQueries({ queryKey: ["case", c.id] });
    };
    window.addEventListener("message", on);
    return () => window.removeEventListener("message", on);
  }, [c.id]);

  const start = useMutation({
    mutationFn: () =>
      api<{ mode: "iframe" | "redirect"; voucherUrl: string }>(`/cases/${c.id}/triage/smed`, {
        method: "POST",
        body: { version: c.version, ...(sex ? { sex } : {}) },
      }),
    onSuccess: (r) => {
      if (r.mode === "redirect") {
        location.assign(r.voucherUrl);
        return;
      }
      setFrameUrl(r.voucherUrl);
      void queryClient.invalidateQueries({ queryKey: ["case", c.id] });
      void queryClient.invalidateQueries({ queryKey: ["smed", c.id] });
    },
  });
  const cancel = useMutation({
    mutationFn: () => api(`/cases/${c.id}/triage/smed`, { method: "DELETE" }),
    onSuccess: () => {
      setFrameUrl(null);
      void queryClient.invalidateQueries({ queryKey: ["smed", c.id] });
    },
  });
  const apply = useMutation({
    mutationFn: () =>
      api(`/cases/${c.id}/triage/smed/apply`, { method: "POST", body: { version: c.version } }),
    onSuccess: (r) => applyCaseResponse(c.id, r),
  });

  const languageSupported = features.data?.smed.supportedLanguages.includes(c.patientLanguage) ?? true;
  const fallback = (
    <Button variant="quiet" icon={<ClipboardList />} onPress={onFallback}>
      {t(import.meta.env.DEV ? "console.smed.demoSkip" : "console.smed.fallback")}
    </Button>
  );

  // Laufende Sitzung im Iframe
  if (frameUrl && session?.status !== "abgeschlossen")
    return (
      <StepFrame
        eyebrow={t("console.step.ersteinschaetzung")}
        title={t("console.smed.title")}
        actions={
          <>
            <Button variant="secondary" icon={<XCircle />} onPress={() => cancel.mutate()}>
              {t("console.smed.cancel")}
            </Button>
          </>
        }
      >
        <iframe
          className={styles.smedFrame}
          src={frameUrl}
          title={t("console.smed.frameTitle")}
          sandbox="allow-scripts allow-forms allow-same-origin"
          referrerPolicy="no-referrer"
        />
      </StepFrame>
    );

  // Ergebnis
  if (session?.status === "abgeschlossen" && session.result) {
    const r = session.result;
    const geeignet = r.mapping.result === "geeignet";
    return (
      <StepFrame
        eyebrow={t("console.step.ersteinschaetzung")}
        title={t("console.smed.resultTitle")}
        actions={
          <>
            <Button
              variant="secondary"
              icon={<RotateCcw />}
              onPress={() => start.mutate()}
              isDisabled={start.isPending}
            >
              {t("console.smed.restart")}
            </Button>
            <Spacer />
            <Button
              size="lg"
              variant={geeignet ? "primary" : "danger"}
              icon={<Stethoscope />}
              onPress={() => apply.mutate()}
              isDisabled={apply.isPending}
            >
              {t("console.smed.apply")}
            </Button>
          </>
        }
      >
        <div className={`${styles.outcome} ${geeignet ? styles.outcomeSuccess : styles.outcomeDanger}`}>
          <p className={styles.outcomeTitle}>
            {t(geeignet ? "console.smed.geeignet" : "console.smed.notfall")}
          </p>
        </div>
        <KeyValueList
          items={[
            { key: "poc", label: t("console.smed.pointOfCare"), value: r.pointOfCareText || EMPTY },
            { key: "ttt", label: t("console.smed.timeToTreat"), value: r.timeToTreatText || EMPTY },
            {
              key: "sym",
              label: t("console.smed.symptom"),
              value: [r.mainSymptom, ...r.relatedSymptoms].filter(Boolean).join(", ") || EMPTY,
            },
            {
              key: "ver",
              label: t("console.smed.verification"),
              value: (
                <Badge tone={session.verified ? "success" : "warning"}>
                  {t(session.verified ? "console.smed.verified" : "console.smed.unverified")}
                </Badge>
              ),
            },
          ]}
        />
        {!session.verified ? <p className={styles.small}>{t("console.smed.unverifiedHint")}</p> : null}
        <ErrorBanner error={apply.error ?? start.error} />
      </StepFrame>
    );
  }

  // Sitzung läuft (z. B. nach Neustart/Sperre): weiter warten oder neu starten
  if (session && OPEN.has(session.status))
    return (
      <StepFrame
        eyebrow={t("console.step.ersteinschaetzung")}
        title={t("console.smed.running")}
        lead={t("console.smed.runningLead")}
        actions={
          <>
            <Button variant="secondary" icon={<XCircle />} onPress={() => cancel.mutate()}>
              {t("console.smed.cancel")}
            </Button>
            <Spacer />
            <Button icon={<RotateCcw />} onPress={() => start.mutate()} isDisabled={start.isPending}>
              {t("console.smed.restart")}
            </Button>
          </>
        }
      >
        <Spinner label={t("console.smed.running")} />
        <ErrorBanner error={start.error} />
      </StepFrame>
    );

  // Start
  return (
    <StepFrame
      eyebrow={t("console.step.ersteinschaetzung")}
      title={t("console.smed.title")}
      lead={t("console.smed.lead")}
      actions={
        <>
          {fallback}
          <Spacer />
          <Button size="lg" icon={<Play />} onPress={() => start.mutate()} isDisabled={start.isPending}>
            {t("console.smed.start")}
          </Button>
        </>
      }
    >
      {session && !OPEN.has(session.status) ? (
        <Banner tone="warning">{t(`console.smed.status.${session.status}`)}</Banner>
      ) : null}
      {!languageSupported ? <Banner tone="info">{t("console.smed.languageHint")}</Banner> : null}
      <div className={styles.grid2}>
        <Select
          label={t("console.smed.sex")}
          description={t("console.smed.sexHint")}
          value={sex}
          placeholder={t("console.smed.sexNone")}
          onChange={(v) => setSex(v as "male" | "female")}
          options={[
            { id: "female", label: t("console.smed.female") },
            { id: "male", label: t("console.smed.male") },
          ]}
        />
      </div>
      <ErrorBanner error={start.error} />
    </StepFrame>
  );
}
